// src/app/services/insurance.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, EMPTY, of } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { environment } from '../environments/environmentCAS';
import { QuoteResponse, ComparisonResult, InsuranceQuoteForm, AprilPayload, AprilPerson, AprilAddress, AprilMobilePhone, AprilProduct, AprilInsured, AprilCoverage } from '../models/project-model';
import { formatDate } from '@angular/common';

import { AprilGetTarifResponse } from '../models/april-models';
import { ApiMapperUtil } from '../utils/api-mapper.util';

@Injectable({ providedIn: 'root' })
export class InsuranceService {
  // Configuration centralisée via environmentCAS
  private apiBasePath = environment.casApiUrl;
  private aprilUrl = `${environment.casApiUrl}${environment.endpoints.april.projects}`;
  private healthProtectionUrl = `${environment.casApiUrl}${environment.endpoints.april.projects}/prices`;
  private utwinUrl = `${environment.casApiUrl}${environment.endpoints.utwin.tarification}`;
  private utwinSouscriptionUrl = `${environment.casApiUrl}${environment.endpoints.utwin.souscription}`;
  private utwinPropositionCommercialeUrl = `${environment.casApiUrl}${environment.endpoints.utwin.propositionCommerciale}`;
  private utwinDocumentsUrl = `${environment.casApiUrl}${environment.endpoints.utwin.documents}`;
  private compareUrl = `${environment.casApiUrl}${environment.apiEndpoint}/v1/comparisons/compare`;
  private apiviaDevisUrl = `${environment.casApiUrl}${environment.endpoints.apivia.devis}`;
  private aprilQuoteUrl = `${environment.casApiUrl}/cas/api/healthProtection/projects`;
  private apiviaPdfUrl = `${environment.casApiUrl}${environment.endpoints.apivia.devis}`;
  private zeniooUrl = `${environment.casApiUrl}${environment.endpoints.zenioo?.tarification || '/api/v1/zenioo/tarification'}`;
  private zeniooProductsUrl = `${environment.casApiUrl}${environment.endpoints.zenioo?.products || '/api/v1/zenioo/products'}`;
  private zeniooFormulasUrl = `${environment.casApiUrl}${environment.endpoints.zenioo?.formulas || '/api/v1/zenioo/formulas'}`;
  private zeniooOptionsUrl = `${environment.casApiUrl}${environment.endpoints.zenioo?.options || '/api/v1/zenioo/options'}`;
  private zeniooQuotationDocumentUrl = `${environment.casApiUrl}${environment.endpoints.zenioo?.quotationDocument || '/api/v1/zenioo/quotation-document'}`;
  private hennerRealiserDiagnostiqueUrl = `${environment.casApiUrl}/api/v1/henner/senior/realiser-diagnostique`;

  private aprilProductCodeMap: { [key: string]: string } = {
    // 🌟 Produits actifs du catalogue courtier APRIL
    'APRIL Moove Santé': 'SanteMoove',
    'Moove Santé': 'SanteMoove',
    'APRIL Santé Optimale': 'SanteOptimale',
    'Santé Optimale': 'SanteOptimale',
    'Santé PRO Privilège APRIL': 'SanteProPrivilege',
    'Santé PRO Privilège': 'SanteProPrivilege',
    'Santé Pro Privilege': 'SanteProPrivilege',
    'Santé PRO Premium': 'SantePremium',
    'Santé Pro Premium': 'SantePremium',
    'Santé PRO Solution': 'SanteSolution',
    'Santé Pro Solution': 'SanteSolution',
    'APRIL Santé Peps': 'SantePeps',
    'Santé Peps': 'SantePeps',
    'APRIL Santé Globale +': 'SanteGlobale',
    'Santé Globale +': 'SanteGlobale',
    'Santé Globale': 'SanteGlobale',
    'Génération Santé GENERALI': 'SanteGenerali',
    'Complémentaire Sénior GAN': 'SanteGan',
    'Complémentaire Senior GAN': 'SanteGan',
    'APRIL Hospi +': 'HospiPlus',
    'Hospi +': 'HospiPlus',
    'Hospi Plus': 'HospiPlus',
    'APRIL Santé Brio': 'SanteBrio',
    'Santé Brio': 'SanteBrio',
    'APRIL Prévoyance Pro Premium': 'PrevPremium',
    'APRIL Frais Généraux Permanents': 'FGP',
    'Frais Généraux Permanents APRIL': 'FGP',
    'Sérénité Obsèques APRIL': 'Obseques',
    'Tempo Décès APRIL': 'Tempo',
    'Accident APRIL': 'Accident',
    'APRIL Santé': 'SanteApril',

    // Autres produits / anciens libellés
    'Protection Santé MALAKOFF HUMANIS': 'SanteProtect',
    'Protection Santé': 'SanteProtect',
    'APRIL Santé Primo': 'SantePrimo',
    'Santé Primo': 'SantePrimo',
    'APRIL Santé Sérénité': 'SanteCapSerenite',
    'Santé Sérénité': 'SanteCapSerenite',
    'APRIL Santé Vita': 'SanteVita',
    'APRIL Vita Santé': 'SanteVita',
    'APRIL Vita Sante': 'SanteVita',
    'Vita Santé': 'SanteVita',
    'APRIL Santé Mix Proximité': 'SanteMix',
    'Santé Mix Proximité': 'SanteMix',
    'APRIL Santé Zen': 'SanteZen',
    'Santé Zen': 'SanteZen',
    'APRIL Santé Eco': 'SanteEco',
    'Santé Eco': 'SanteEco',
    'SANTE PRO APRIL / SURCOMPLEMENTAIRE PRO APRIL': 'SantePro',
    'Santé Pro Start APRIL': 'SanteProStart',
    'Santé Pro Start': 'SanteProStart',
    'APRIL Tranquillité Santé': 'SanteTranquil',
    'Tranquillité Santé': 'SanteTranquil',
    'APRIL Santé Réflexe': 'SanteReflexe',
    'Santé Réflexe': 'SanteReflexe',
    'APRIL Only Santé': 'OnlySante',
    'Only Santé': 'OnlySante',
    'APRIL FLEXI SANTE': 'FlexiSante',
    'Flexi Santé': 'FlexiSante',
    'Simply Santé': 'SimplySante',
    'APRIL - Simply Santé': 'SimplySante',
    'APRIL Simply Santé': 'SimplySante',

    // Libellés génériques en dernier
    'Santé Mix': 'SanteMix',
    'Santé Pro': 'SantePro',
  };

  constructor(
    private http: HttpClient
  ) { }

  getHennerRealiserDiagnostique(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(this.hennerRealiserDiagnostiqueUrl, payload, { headers }).pipe(
      retry(1),
      catchError(error => {
        console.error('Erreur lors de la tarification Henner (RealiserDiagnostique):', error);
        throw error;
      })
    );
  }

  getApiviaTarif(data: any): Observable<any> {
    // Construction du payload JSON pour le backend selon ApiviaTarificationRequest
    const payload = {
      action: data.action || 'tarification',
      format: data.format || 'json',
      produits: data.produits || '',
      formules: data.formules || '',
      renforts: data.renforts || '',
      options: data.options || '',
      codePostal: data.codePostal,
      dateEffet: data.dateEffet,
      dejaAssurance: data.dejaAssurance || false,
      isResiliationPourCompte: data.isResiliationPourCompte || false,
      typeAffaire: data.typeAffaire || '',
      typeObjectSortie: data.typeObjectSortie || 'v2',
      beneficiaires: data.beneficiaires || []
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    const apiUrl = `${environment.casApiUrl}${environment.endpoints.apivia.tarification}`;

    return this.http.post<any>(apiUrl, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la tarification Apivia:', error);
        throw error;
      })
    );
  }

  generateApiviaDevisPdf(devisRequest: any, test: boolean = false): Observable<Blob> {
    const params = new HttpParams().set('test', test.toString());

    return this.http.post(this.apiviaDevisUrl, devisRequest, {
      params,
      responseType: 'blob'
    }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la génération du devis Apivia:', error);
        throw error;
      })
    );
  }

  sendAprilQuoteByEmail(payload: any): Observable<any> {
    const params = new HttpParams().set('marketingParameters.requestType', 'Quotation');
    return this.http.post(this.aprilQuoteUrl, payload, { params }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'envoi du devis April par email:', error);
        throw error;
      })
    );
  }

  generateCommercialProposal(payload: any, action: 'envoiParEmail' | 'telechargement'): Observable<any> {
    const endpoint = `${this.apiBasePath}/cas/api/healthProtection/projects/commercial-proposal`;
    const params = new HttpParams().set('action', action);
    return this.http.post(endpoint, payload, { params }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la génération de la proposition commerciale:', error);
        throw error;
      })
    );
  }

  generateCommercialProposalPdf(payload: any): Observable<Blob> {
    const endpoint = `${this.apiBasePath}/cas/api/healthProtection/projects/commercial-proposal`;
    const params = new HttpParams().set('action', 'telechargement');

    return this.http.post(endpoint, payload, {
      params,
      responseType: 'blob'
    }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors du téléchargement de la proposition commerciale (PDF):', error);
        throw error;
      })
    );
  }

  generateAprilQuotePdf(payload: any): Observable<Blob> {
    const endpoint = `${this.apiBasePath}/cas/api/healthProtection/projects/commercial-proposal/april-pdf`;
    return this.http.post(endpoint, payload, {
      responseType: 'blob'
    }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors du téléchargement du PDF APRIL:', error);
        throw error;
      })
    );
  }

  /**
   * Récupère les documents de devis APRIL (base64) pour l'email multi-offres.
   * Backend: crée un projet Quotation puis résout/télécharge les pièces jointes.
   */
  generateAprilQuoteDocuments(payload: any): Observable<any> {
    const endpoint = `${this.apiBasePath}/cas/api/healthProtection/projects/april-quote-documents`;
    return this.http.post<any>(endpoint, payload).pipe(
      retry(1),
      catchError(error => {
        console.error('Erreur lors de la récupération des documents APRIL:', error);
        throw error;
      })
    );
  }

  /**
   * Génère un PDF de devis simple via le backend (PDFBox).
   * Utilisé pour les assureurs qui n'ont pas d'API dédiée de génération PDF
   * (ex : Alptis Frontaliers Suisses).
   * Retourne un JSON avec { contentBase64, contentType, size }.
   */
  generateSimpleDevisPdf(data: any): Observable<any> {
    const endpoint = `${this.apiBasePath}/cas/api/v1/pdf/generate-devis`;
    return this.http.post<any>(endpoint, data).pipe(
      retry(1),
      catchError(error => {
        console.error('Erreur lors de la génération du PDF simple:', error);
        throw error;
      })
    );
  }

  /**
   * Récupère un document UTWIN par son identifiant
   */
  getUtwinDocument(identifiant: string): Observable<Blob> {
    const endpoint = `${this.apiBasePath}/api/Sante/v1/Documents/${identifiant}`;
    const headers = new HttpHeaders({
      'Accept': 'application/pdf'
    });
    return this.http.get(endpoint, {
      headers: headers,
      responseType: 'blob'
    }).pipe(
      retry(2),
      catchError(error => {
        console.error(`Erreur lors de la récupération du document UTWIN ${identifiant}:`, error);
        throw error;
      })
    );
  }

  downloadApiviaQuotePdf(payload: any): Observable<Blob> {
    return this.http.post(this.apiviaPdfUrl, payload, {
      responseType: 'blob'
    }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors du téléchargement du PDF Apivia:', error);
        throw error;
      })
    );
  }

  // New method for Health Protection API


  private getGuaranteeCoverages(productCode: string, insuredRef: string, levelCode: string, isMadelinEligible: boolean): AprilCoverage[] {
    const coverages: AprilCoverage[] = [];

    const code = (productCode || '').toString();
    const codeLower = code.toLowerCase();

    let guarantees: string[];
    if (codeLower.startsWith('santemix')) {
      guarantees = ['GarantieHospitalisation', 'GarantieFraisDeSante'];
    } else if (codeLower.startsWith('flexisante')) {
      guarantees = ['GarantieHospitalisation', 'GarantieDentaireOptiqueAuditives'];
    } else if (codeLower === 'santepro' || codeLower === 'santeprostart' || codeLower === 'santesolution') {
      // 🚨 CORRECTION: Ne pas ajouter Surcomplementaire si MaladieChirurgie est au niveau 01 (incompatible selon April API)
      guarantees = ['MaladieChirurgie'];
      if (levelCode !== '01') {
        guarantees.push('Surcomplementaire');
      }
    } else {
      guarantees = ['MaladieChirurgie'];
    }

    guarantees.forEach(guaranteeCode => {
      const coverage: AprilCoverage = {
        insured: { $ref: insuredRef },
        guaranteeCode: guaranteeCode,
        eligibleMadelinLaw: isMadelinEligible
      };

      // Appliquer le levelCode selon les règles
      if (codeLower === 'santepro' || codeLower === 'santeprostart' || codeLower === 'santesolution') {
        if (guaranteeCode !== 'Surcomplementaire') {
          coverage.levelCode = levelCode;
        }
      } else if (codeLower.startsWith('santemix')) {
        coverage.levelCode = levelCode;
      } else {
        // Pour tous les autres produits santé April, appliquer le levelCode par défaut
        coverage.levelCode = levelCode;
      }

      coverages.push(coverage);
    });

    return coverages;
  }

  private transformToAprilPayload(form: InsuranceQuoteForm, productCode: string, levelCode: string, commissionRate?: string): AprilPayload {
    const mainPersonInfo = form.insuredPersons[0];

    const persons: AprilPerson[] = form.insuredPersons.map((p, index) => {
      const personId = `i-${index + 1}`;
      return {
        $id: personId,
        birthDate: formatDate(p.birthDate, 'yyyy-MM-dd', 'en-US'),
        title: p.gender === 'M' ? 'Monsieur' : 'Madame',
        lastName: p.lastName,
        firstName: p.firstName,
        mandatoryScheme: ApiMapperUtil.getAprilMandatoryScheme(p.regime),
        familyStatus: p.situation || 'Celibataire',
        mobilePhone: {
          prefix: '+33',
          number: (p.phoneNumber?.replace(/\s|\(|\)/g, '') || '').replace(/^(\+33|33)/, '0') // Normalise le numéro (ex: +336... -> 06...)
        },
        email: p.email || ''
      };
    });

    const insureds: AprilInsured[] = form.insuredPersons.map((p, index) => {
      const insuredId = `a-${index + 1}`;
      let role: 'AssurePrincipal' | 'Conjoint' | 'Enfant' = 'Enfant';
      if (index === 0) {
        role = 'AssurePrincipal';
      } else if (index === 1 && form.insuredPersons.length > 1 && p.situation) { // crude check for spouse
        role = 'Conjoint';
      }
      return {
        $id: insuredId,
        role: role,
        person: { $ref: `i-${index + 1}` }
      };
    });

    // Générer les coverages pour tous les assurés
    const allCoverages: AprilCoverage[] = [];
    const mainMandatoryScheme = ApiMapperUtil.getAprilMandatoryScheme(mainPersonInfo.regime);
    const isMadelinEligible = mainMandatoryScheme === 'TNS';

    insureds.forEach((insured, index) => {
      const insuredRef = `a-${index + 1}`;
      const insuredCoverages = this.getGuaranteeCoverages(productCode, insuredRef, levelCode, isMadelinEligible);
      allCoverages.push(...insuredCoverages);
    });

    console.log(`✅ Coverages générées pour ${insureds.length} assurés:`, allCoverages);

    const payload: AprilPayload = {
      $type: 'PrevPro',
      properties: {
        addresses: [
          {
            $id: 'adr-1',
            type: 'Actuelle',
            addressLine1: mainPersonInfo.address.street,
            postCode: mainPersonInfo.address.postalCode,
            city: mainPersonInfo.address.city
          }
        ],
        email: mainPersonInfo.email
      },
      persons: persons,
      products: [
        {
          $id: 'p-1',
          productCode: productCode,
          effectiveDate: formatDate(form.effectDate, 'yyyy-MM-dd', 'en-US'),
          // N'inclure le commissionnement que si explicitement renseigné (évite une erreur 412 April)
          ...(commissionRate ? { commission: commissionRate } : {}),
          insureds: insureds,
          coverages: allCoverages
        }
      ]
    };

    return payload;
  }

  getAprilHealthTarif(form: InsuranceQuoteForm, productReference: string, formule: string, commissionRate?: string): Observable<any> {
    // Extraire le levelCode de la formule (ex: "Formule 3" -> "03")
    const levelMatch = formule.match(/\d+/);
    const levelCode = levelMatch ? levelMatch[0].padStart(2, '0') : '01'; // Default à '01' si non trouvé

    const normalizeReference = (value: string): string => {
      return (value || '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\+/g, ' plus ')
        .replace(/[^a-z0-9 ]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    };

    // Déterminer le productCode en cherchant une correspondance (les plus spécifiques d'abord)
    let productCode: string | null = null;
    const normalizedProductReference = normalizeReference(productReference);
    const sortedKeys = Object.keys(this.aprilProductCodeMap).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
      if (normalizedProductReference.includes(normalizeReference(key))) {
        productCode = this.aprilProductCodeMap[key];
        break;
      }
    }

    // Si aucun productCode n'est trouvé pour un produit April, retourner une erreur
    if (!productCode) {
      // Ne pas traiter comme une erreur si ce n'est pas un produit April, retourner simplement un observable vide.
      if (!productReference.toLowerCase().includes('april')) {
        return EMPTY;
      }
      console.error('Aucun productCode APRIL correspondant trouvé pour la référence:', productReference);
      return throwError(() => new Error(`Aucun produit APRIL correspondant trouvé pour "${productReference}"`));
    }

    const payload = this.transformToAprilPayload(form, productCode, levelCode, commissionRate);

    console.log('Sending April Payload:', JSON.stringify(payload, null, 2));

    const url = this.healthProtectionUrl; // Utiliser l'URL correcte du backend

    // Ajout des paramètres de requête attendus par le backend
    const params = new HttpParams()
      .set('pricingType', 'Simple') // Valeur par défaut
      .set('withSchedule', 'true');   // Valeur par défaut

    // Headers selon l'ancienne version qui fonctionne
    const projectUuid = this.generateRandomUuid();
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache',
      'x-projectUuid': projectUuid,
      'x-api-version': '1.0'
    });

    return this.http.post(url, payload, { headers, params }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la tarification April Health:', error);
        throw error;
      })
    );
  }

  private generateRandomUuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  getUtwinTarif(
    form: InsuranceQuoteForm,
    formule: string,
    codeProduit: string = 'UTWIN_MULTI',
    tauxCommission?: string
  ): Observable<any> {
    const formatDate = (date: any): string => {
      try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return new Date().toISOString().split('T')[0];
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (e) {
        return new Date().toISOString().split('T')[0];
      }
    };

    const mapRegime = (regime: string): string => {
      return ApiMapperUtil.getUtwinRegime(regime);
    };

    const mapSexe = (gender: string): string => {
      if (!gender) return 'M';
      const g = gender.toUpperCase();
      if (g === 'M' || g === 'MONSIEUR' || g === 'GARCON') return 'M';
      if (g === 'F' || g === 'MADAME' || g === 'FILLE') return 'F';
      return 'M';
    };

    const mapCivilite = (gender: string): string => {
      if (!gender) return 'Monsieur';
      const g = gender.toUpperCase();
      if (g === 'M' || g === 'MONSIEUR' || g === 'GARCON') return 'Monsieur';
      if (g === 'F' || g === 'MADAME' || g === 'FILLE') return 'Madame';
      return 'Monsieur';
    };

    const mapStatut = (situation: string): string => {
      if (!situation) return 'CELIBATAIRE';
      const s = situation.toUpperCase();
      if (s.includes('MARIE') || s.includes('UNION_LIBRE') || s.includes('CONJOINT')) return 'MARIE';
      if (s.includes('PACSE')) return 'PACSE';
      if (s.includes('DIVORCE')) return 'DIVORCE';
      if (s.includes('VEUF')) return 'VEUF';
      return 'CELIBATAIRE';
    };

    const extractFormulaCode = (f: string): string => {
      if (!f) return 'N1';
      const match = f.match(/\d+/);
      return match ? `N${match[0]}` : 'N1';
    };

    const payload = {
      souscripteur: {
        adresse: {
          codePostal: form.contact?.address?.postalCode || '69000',
          ville: form.contact?.address?.city || 'Lyon'
        }
      },
      besoin: {
        dateEffet: formatDate(form.effectDate || new Date())
      },
      assures: form.insuredPersons?.map((person, index) => {
        let codeTypeRole = 'Enfant';
        if (index === 0) {
          codeTypeRole = 'AssurePrincipal';
        } else if (index === 1) {
          codeTypeRole = 'Conjoint';
        }

        return {
          codeRegimeObligatoire: mapRegime(person.regime),
          codeTypeRole: codeTypeRole,
          codeSexe: mapSexe(person.gender),
          codeCivilite: mapCivilite(person.gender),
          codeStatutFamilial: mapStatut(person.situation),
          dateDeNaissance: formatDate(person.birthDate || new Date())
        };
      }) || [],
      contexteDeVente: {
        produits: [{
          codeProduit: codeProduit || 'UTWIN_MULTI',
          codeFormule: extractFormulaCode(formule),
          ...(tauxCommission ? { tauxCommission } : {}),
          versionProduit: 1
        }],
        venteAppelNonSollicite: false
      }
    };

    console.log('Sending Utwin Payload:', JSON.stringify(payload, null, 2));

    // Headers Utwin
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-U-Licence': '6519cef5-168d-4931-b15d-c1ca46ee59f5',
      'X-Utwin-Contexte-Vendeur': 'MALA00759'
    });

    return this.http.post(this.utwinUrl, payload, { headers }).pipe(
      retry(2),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400 && error.error) {
          console.warn('Backend returned 400, but treating as success:', error.error);
          return of(error.error);
        }
        console.error('Erreur lors de la tarification Utwin:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Lance une souscription UTWIN Santé.
   * Crée une proposition commerciale, initialise un parcours de souscription
   * et retourne un lien d'accès pour le client.
   */
  utwinSouscription(
    form: InsuranceQuoteForm | any,
    formule: string,
    codeProduit: string = 'UTWIN_MULTI',
    tauxCommission?: string
  ): Observable<any> {
    const formatDateFn = (date: any): string => {
      try {
        if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
          const [day, month, year] = date.split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        const d = new Date(date);
        if (isNaN(d.getTime())) return new Date().toISOString().split('T')[0];
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (e) {
        return new Date().toISOString().split('T')[0];
      }
    };

    const mapRegime = (regime: string): string => {
      return ApiMapperUtil.getUtwinRegime(regime);
    };

    const mapCivilite = (gender: string): string => {
      if (!gender) return 'Monsieur';
      const g = gender.toUpperCase();
      if (g === 'M' || g === 'MONSIEUR' || g === 'GARCON') return 'Monsieur';
      if (g === 'F' || g === 'MADAME' || g === 'FILLE') return 'Madame';
      return 'Monsieur';
    };

    const extractFormulaCode = (f: string): string => {
      if (!f) return 'N1';
      const match = f.match(/\d+/);
      return match ? `N${match[0]}` : 'N1';
    };

    const payload = this.buildUtwinPayload(form as any, formule, codeProduit, {
      defaultCommission: tauxCommission || '30/10',
      formatDateFn,
      mapRegime,
      mapCivilite,
      extractFormulaCode
    });

    console.log('🚀 Sending Utwin Souscription Payload:', JSON.stringify(payload, null, 2));

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post(this.utwinSouscriptionUrl, payload, { headers }).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ Erreur lors de la souscription Utwin:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crée une proposition commerciale UTWIN Santé.
   * @param action 'telechargement' ou 'envoiParEmail'
   */
  utwinPropositionCommerciale(form: InsuranceQuoteForm | any, formule: string, codeProduit: string = 'UTWIN_MULTI', action: 'telechargement' | 'envoiParEmail' = 'telechargement'): Observable<any> {

    const formatDateFn = (date: any): string => {
      try {
        if (typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
          const [day, month, year] = date.split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        const d = new Date(date);
        if (isNaN(d.getTime())) return new Date().toISOString().split('T')[0];
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (e) {
        return new Date().toISOString().split('T')[0];
      }
    };

    const mapRegime = (regime: string): string => {
      return ApiMapperUtil.getUtwinRegime(regime);
    };

    const mapCivilite = (gender: string): string => {
      if (!gender) return 'Monsieur';
      const g = gender.toUpperCase();
      if (g === 'M' || g === 'MONSIEUR' || g === 'GARCON') return 'Monsieur';
      if (g === 'F' || g === 'MADAME' || g === 'FILLE') return 'Madame';
      return 'Monsieur';
    };

    const extractFormulaCode = (f: string): string => {
      if (!f) return 'N1';
      const match = f.match(/\d+/);
      return match ? `N${match[0]}` : 'N1';
    };

    const payload: any = this.buildUtwinPayload(form as any, formule, codeProduit, {
      defaultCommission: '30/10',
      formatDateFn,
      mapRegime,
      mapCivilite,
      extractFormulaCode
    });

    console.log('📄 Sending Utwin Proposition Commerciale Payload:', JSON.stringify(payload, null, 2));

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    const params = new HttpParams().set('action', action);

    return this.http.post(this.utwinPropositionCommercialeUrl, payload, { headers, params }).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ Erreur lors de la proposition commerciale Utwin:', error);
        return throwError(() => error);
      })
    );
  }

  private buildUtwinPayload(
    raw: any,
    formule: string,
    codeProduit: string,
    helpers: {
      defaultCommission: string;
      formatDateFn: (date: any) => string;
      mapRegime: (regime: string) => string;
      mapCivilite: (gender: string) => string;
      extractFormulaCode: (f: string) => string;
    }
  ): any {
    const { defaultCommission, formatDateFn, mapRegime, mapCivilite, extractFormulaCode } = helpers;
    const mainPerson = raw?.insuredPersons?.[0];
    const incomingAssures = Array.isArray(raw?.assures) ? raw.assures : [];

    const mappedAssures = incomingAssures.length > 0
      ? incomingAssures.map((assure: any, index: number) => {
          const role = assure?.codeTypeRole || (index === 0 ? 'AssurePrincipal' : index === 1 ? 'Conjoint' : 'Enfant');
          const fallback = raw?.insuredPersons?.[index] || mainPerson || {};
          const codeCivilite = assure?.codeCivilite || mapCivilite(fallback?.gender);
          return {
            ...assure,
            codeTypeRole: role,
            codeRegimeObligatoire: assure?.codeRegimeObligatoire || mapRegime(fallback?.regime),
            codeCivilite: codeCivilite || 'Monsieur',
            nom: assure?.nom || fallback?.lastName || 'NOM',
            prenom: assure?.prenom || fallback?.firstName || 'PRENOM',
            dateDeNaissance: formatDateFn(assure?.dateDeNaissance || fallback?.birthDate || new Date())
          };
        })
      : (raw?.insuredPersons?.map((person: any, index: number) => {
          const codeTypeRole = index === 0 ? 'AssurePrincipal' : index === 1 ? 'Conjoint' : 'Enfant';
          return {
            codeRegimeObligatoire: mapRegime(person?.regime),
            codeTypeRole,
            codeCivilite: mapCivilite(person?.gender),
            nom: person?.lastName || 'NOM',
            prenom: person?.firstName || 'PRENOM',
            dateDeNaissance: formatDateFn(person?.birthDate || new Date())
          };
        }) || []);

    const principal = mappedAssures.find((a: any) => a.codeTypeRole === 'AssurePrincipal') || mappedAssures[0];
    const rawStreet =
      raw?.souscripteur?.adresse?.nomVoie ||
      raw?.contact?.address?.street ||
      mainPerson?.address?.street ||
      '';

    const normalizedStreet = rawStreet.toString().trim().replace(/\s+/g, ' ');
    const streetNumberMatch = normalizedStreet.match(/^(\d+[A-Za-z]?)\s+(.*)$/);
    const numeroVoie = raw?.souscripteur?.adresse?.numeroVoie || streetNumberMatch?.[1] || '1';
    const streetWithoutNumber = (streetNumberMatch?.[2] || normalizedStreet).trim();
    const typeMatch = streetWithoutNumber.match(/^(RUE|AVENUE|AV|BOULEVARD|BD|CHEMIN|CH|IMPASSE|ALLEE|ALL[ÉE]E|PLACE|PL|ROUTE|RTE|QUAI|QAI)\b/i);
    const typeVoie = raw?.souscripteur?.adresse?.typeVoie || (typeMatch ? typeMatch[1].toUpperCase() : 'RUE');
    const nomVoie =
      raw?.souscripteur?.adresse?.nomVoie ||
      (typeMatch ? streetWithoutNumber.replace(typeMatch[0], '').trim() : streetWithoutNumber) ||
      'INCONNU';

    return {
      ...raw,
      souscripteur: {
        ...(raw?.souscripteur || {}),
        telephonePortable:
          raw?.souscripteur?.telephonePortable ||
          mainPerson?.phoneNumber ||
          '0600000000',
        email:
          raw?.souscripteur?.email ||
          mainPerson?.email ||
          '',
        nom:
          raw?.souscripteur?.nom ||
          principal?.nom ||
          mainPerson?.lastName ||
          'NOM',
        prenom:
          raw?.souscripteur?.prenom ||
          principal?.prenom ||
          mainPerson?.firstName ||
          'PRENOM',
        codeCivilite:
          raw?.souscripteur?.codeCivilite ||
          principal?.codeCivilite ||
          mapCivilite(mainPerson?.gender),
        adresse: {
          ...(raw?.souscripteur?.adresse || {}),
          numeroVoie,
          typeVoie,
          nomVoie,
          codePostal:
            raw?.souscripteur?.adresse?.codePostal ||
            raw?.contact?.address?.postalCode ||
            mainPerson?.address?.postalCode ||
            '69000',
          ville:
            raw?.souscripteur?.adresse?.ville ||
            raw?.contact?.address?.city ||
            mainPerson?.address?.city ||
            'LYON',
          pays: raw?.souscripteur?.adresse?.pays || 'FR'
        }
      },
      besoin: {
        ...(raw?.besoin || {}),
        dateEffet: formatDateFn(raw?.besoin?.dateEffet || raw?.effectDate || new Date())
      },
      assures: mappedAssures,
      contexteDeVente: {
        ...(raw?.contexteDeVente || {}),
        produits: [{
          ...(raw?.contexteDeVente?.produits?.[0] || {}),
          codeProduit: raw?.contexteDeVente?.produits?.[0]?.codeProduit || codeProduit || 'UTWIN_MULTI',
          codeFormule: raw?.contexteDeVente?.produits?.[0]?.codeFormule || extractFormulaCode(formule),
          tauxCommission: raw?.contexteDeVente?.produits?.[0]?.tauxCommission || defaultCommission
        }],
        venteAppelNonSollicite: raw?.contexteDeVente?.venteAppelNonSollicite ?? false
      }
    };
  }

  /**
   * Récupère un document UTWIN par son identifiant technique.
   */
  getUtwinDocumentById(identifiant: string): Observable<any> {
    const url = `${this.utwinDocumentsUrl}/${identifiant}`;
    const headers = new HttpHeaders({
      'Accept': 'application/json'
    });
    return this.http.get(url, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error(`❌ Erreur lors de la récupération du document UTWIN ${identifiant}:`, error);
        throw error;
      })
    );
  }

  getEcaTarif(payload: any): Observable<any> {
    const url = `${environment.flaskUrl}${environment.flaskEndpoints.tarif}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(url, payload, { headers }).pipe(
      catchError(error => {
        console.error('Erreur lors de la tarification ECA (Flask):', error);
        throw error;
      })
    );
  }

  getZeniooTarif(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(this.zeniooUrl, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de la tarification Zenioo:', error);
        throw error;
      })
    );
  }

  getZeniooProducts(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Accept': 'application/json'
    });

    let params = new HttpParams();
    if (payload && typeof payload === 'object') {
      Object.entries(payload).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          params = params.set(k, String(v));
        }
      });
    }

    return this.http.get<any>(this.zeniooProductsUrl, { headers, params }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo GetProducts:', error);
        throw error;
      })
    );
  }

  getZeniooFormulas(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Accept': 'application/json'
    });

    let params = new HttpParams();
    if (payload && typeof payload === 'object') {
      Object.entries(payload).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          params = params.set(k, String(v));
        }
      });
    }

    return this.http.get<any>(this.zeniooFormulasUrl, { headers, params }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo GetFormulas:', error);
        throw error;
      })
    );
  }

  getZeniooOptions(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(this.zeniooOptionsUrl, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo GetOptions:', error);
        throw error;
      })
    );
  }

  getZeniooQuotationDocument(payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(this.zeniooQuotationDocumentUrl, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo GetQuotationDocument:', error);
        throw error;
      })
    );
  }

  getZeniooDetailedOffer(payload: any): Observable<any> {
    const url = `${environment.casApiUrl}/api/v1/zenioo/detailed-offer`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(url, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo GetDetailedOffer:', error);
        throw error;
      })
    );
  }

  getZeniooSubscriptionUrl(payload: any): Observable<any> {
    const url = `${environment.casApiUrl}/api/v1/zenioo/subscription-form`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(url, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo GoToSubscriptionForm:', error);
        throw error;
      })
    );
  }

  getZeniooSubscriptionDocument(payload: any): Observable<any> {
    const url = `${environment.casApiUrl}/api/v1/zenioo/subscription-document`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(url, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo GetSubscriptionDocument:', error);
        throw error;
      })
    );
  }

  uploadZeniooSignedDocument(payload: any): Observable<any> {
    const url = `${environment.casApiUrl}/api/v1/zenioo/upload-signed-document`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(url, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo UploadSignedDocument:', error);
        throw error;
      })
    );
  }

  validateZeniooSubscription(payload: any): Observable<any> {
    const url = `${environment.casApiUrl}/api/v1/zenioo/validate-subscription`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(url, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo ValidateSubscription:', error);
        throw error;
      })
    );
  }

  uploadZeniooDocumentToSign(payload: any): Observable<any> {
    const url = `${environment.casApiUrl}/api/v1/zenioo/upload-document-to-sign`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(url, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo UploadDocumentToSign:', error);
        throw error;
      })
    );
  }

  sendZeniooESignatureLink(payload: any): Observable<any> {
    const url = `${environment.casApiUrl}/api/v1/zenioo/send-esignature-link`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(url, payload, { headers }).pipe(
      retry(2),
      catchError(error => {
        console.error('Erreur lors de l\'appel Zenioo SendeSignatureLink:', error);
        throw error;
      })
    );
  }
}